import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, DialogActions, DialogContent, DialogTitle, Step, StepLabel, Stepper } from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import useSettings from '../hooks/useSettings';
import useUserData from '../hooks/useUserData';
import { executeApi } from '../Utils';

import TemplateStep from './steps/TemplateStep';
import ParametersStep, { type ReportParameter } from './steps/ParametersStep';
import ResultStep from './steps/ResultStep';

interface ReportingDialogProps {
	module: string;
	folder: string;
	defaultParameters: ReportParameter<any>[];
}

export function showReportingDialog(props: ReportingDialogProps) {
	return openDialog({
		children: <ReportingDialog {...props} />,
		fullWidth: true,
		fullScreen: false,
		maxWidth: 'sm'
	});
}

function formatParameters(params) {
	const formattedParams = {};
	params.forEach(parameter => {
		formattedParams[parameter.id] = parameter.state.value;
	});
	return formattedParams;
}

export default function ReportingDialog({ module, folder, defaultParameters }: ReportingDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();
	const userData = useUserData();

	const [activeStepIndex, setActiveStepIndex] = React.useState(0);
	const previousStep = () => {
		if (reportBlobUrl) setReportBlobUrl(undefined);
		if (activeStepIndex === 0) dispatch(closeDialog());
		else setActiveStepIndex(stepIndex => stepIndex - 1);
	};
	const nextStep = () => {
		if (activeStepIndex === steps.length - 1) dispatch(closeDialog());
		else setActiveStepIndex(stepIndex => stepIndex + 1);
	};

	const [reportUri, setReportUri] = React.useState<string>();
	const [dataValid, setDataValid] = React.useState(true);

	const [fileName, setFileName] = React.useState('report');
	const [exportFormat, setExportFormat] = React.useState('pdf');
	const [parameters, setParameters] = React.useState<any[]>([]);

	const [isProcessing, setIsProcessing] = React.useState(true);
	const [reportBlobUrl, setReportBlobUrl] = React.useState<string>();

	function generateReport() {
		setIsProcessing(true);
		nextStep();

		const extraParams = { REPORT_LOCALE: userData.user_language };

		executeApi.post({
			url: `/api/reporting/reports/generate${reportUri}`,
			body: {
				ReportFormat: exportFormat,
				ReportFileName: `${fileName}.${exportFormat}`,
				Parameters: { ...formatParameters(parameters), ...extraParams }
			},
			config: { responseType: 'blob' },
			dispatch,
			successHandler: response => {
				const blob = new Blob([response.data], { type: response.headers['content-type'] });
				setReportBlobUrl(window.URL.createObjectURL(blob));
				setIsProcessing(false);
			}
		});
	}

	const steps = [
		{
			label: t('REPORTING_STEP_TEMPLATE'),
			content: (
				<TemplateStep
					module={module}
					folder={folder}
					reportUri={reportUri}
					setReportUri={setReportUri}
					defaultParameters={defaultParameters}
					setParameters={setParameters}
				/>
			),
			handleNextStep: () => {
				nextStep();
			},
			disableNext: !reportUri
		},
		{
			label: t('REPORTING_STEP_PARAMETERS'),
			content: (
				<ParametersStep
					fileName={fileName}
					setFileName={setFileName}
					exportFormat={exportFormat}
					setExportFormat={setExportFormat}
					parameters={parameters}
					setParameters={setParameters}
					setDataValid={setDataValid}
				/>
			),
			handleNextStep: () => {
				generateReport();
			},
			disableNext: !dataValid
		},
		{
			label: t('REPORTING_STEP_RESULT'),
			content: (
				<ResultStep
					fileName={fileName}
					exportFormat={exportFormat}
					reportUri={reportUri}
					reportBlobUrl={reportBlobUrl}
					isProcessing={isProcessing}
				/>
			),
			handleNextStep: () => {}
		}
	];

	const activeStep = steps[activeStepIndex];

	return (
		<>
			<DialogTitle>{t('DIALOG_TITLE_REPORTING')}</DialogTitle>
			<DialogContent className="py-0">
				<Stepper className="pb-16" activeStep={activeStepIndex} alternativeLabel>
					{steps.map((step, index) => (
						<Step key={index}>
							<StepLabel>{step.label}</StepLabel>
						</Step>
					))}
				</Stepper>
				{activeStep.content}
			</DialogContent>
			<DialogActions>
				{activeStepIndex !== 0 && <Button onClick={previousStep}>{t('DIALOG_ACTION_BACK')}</Button>}
				<Button onClick={() => dispatch(closeDialog())}>
					{t(activeStepIndex === steps.length - 1 ? 'DIALOG_ACTION_CLOSE' : 'DIALOG_ACTION_CANCEL')}
				</Button>
				{activeStepIndex < steps.length - 1 && (
					<Button color="secondary" onClick={activeStep.handleNextStep} disabled={activeStep.disableNext}>
						{t('DIALOG_ACTION_NEXT')}
					</Button>
				)}
			</DialogActions>
		</>
	);
}
